import { produce } from 'immer';
import { createSelector } from 'reselect';
import { find } from 'lodash-es';
import * as api from '../../lib/api';
import * as mappingService from '../../services/mapping-service';
import { ScopedType, ScopedAsyncType } from './scoped-type';
import { asyncAction } from './async-action';

const Type = ScopedType('SOYRX');
const AsyncType = ScopedAsyncType('SOYRX');

const LOAD_SOYRX_FOR_GROWER_BEGIN = Type('LOAD_SOYRX_FOR_GROWER_BEGIN');
const LOAD_SOYRX_FOR_GROWER_END = Type('LOAD_SOYRX_FOR_GROWER_END');
const LOAD_SOYRX_FOR_GROWER_ERROR = Type('LOAD_SOYRX_FOR_GROWER_ERROR');
const LOAD_COLORS = AsyncType('LOAD_COLORS');
const SET_IS_LOADING = Type('SET_IS_LOADING');
const SET_ADJUSTMENT_FIELD = Type('SET_ADJUSTMENT_FIELD');
const SLICE = 'soyrx';

const initialState = {
  isLoading: false,
  error: null,
  fields: [],
  adjustmentField: null,
  selectedSoyFields: [],
};

export const selectors = {
  isLoading: (state) => state[SLICE].isLoading,
  fields: (state) => state[SLICE].fields,
  adjustmentField: (state) => state[SLICE].adjustmentField,
  earlyChartColors: (state) => state[SLICE].layerColors?.early,
  lateChartColors: (state) => state[SLICE].layerColors?.late,
  earlyGroupBreaks: createSelector(
    (state) => state[SLICE].layerColors,
    (layerColors) => Object.keys(layerColors?.early || {}).map(Number),
  ),
  lateGroupBreaks: createSelector(
    (state) => state[SLICE].layerColors,
    (layerColors) => Object.keys(layerColors?.late || {}).map(Number),
  ),
  selectedSoyFields: createSelector(
    (state) => state[SLICE].fields,
    (state) => state.fields.selectedGrowerFields,
    (moduleFields, selectedGrowerFields) =>
      moduleFields.filter((field) =>
        find(selectedGrowerFields, {
          aaManagedId: field.aaManagedId,
        }),
      ),
  ),
};

export const actions = {
  loadSoyRxForGrower,
  setIsLoading,
  setAdjustmentField,
  loadLayerMeta,
};

function loadSoyRxForGrowerBegin({ shouldClear }) {
  return {
    type: LOAD_SOYRX_FOR_GROWER_BEGIN,
    payload: { shouldClear },
  };
}

function loadSoyRxForGrowerEnd(data) {
  return {
    type: LOAD_SOYRX_FOR_GROWER_END,
    payload: data,
  };
}

function loadSoyRxForGrowerError(error) {
  return {
    type: LOAD_SOYRX_FOR_GROWER_ERROR,
    payload: error,
  };
}

function setIsLoading(isLoading) {
  return {
    type: SET_IS_LOADING,
    payload: isLoading,
  };
}

function setAdjustmentField(aaid) {
  return {
    type: SET_ADJUSTMENT_FIELD,
    payload: aaid,
  };
}

function loadSoyRxForGrower(growerId, { shouldClear = true } = {}) {
  return (dispatch) => {
    dispatch(loadSoyRxForGrowerBegin({ shouldClear }));

    return api.getSoyRxForGrower(growerId).then(
      (data) => dispatch(loadSoyRxForGrowerEnd(data)),
      (error) => dispatch(loadSoyRxForGrowerError(error)),
    );
  };
}

function loadLayerMeta() {
  return asyncAction(LOAD_COLORS, async () => {
    const [early, late, earlyProperties, lateProperties] = await Promise.all([
      mappingService.getLayerChartColors('soyrx-early'),
      mappingService.getLayerChartColors('soyrx-late'),
      mappingService.getLayerProperties('soyrx-early'),
      mappingService.getLayerProperties('soyrx-late'),
    ]);
    return { early, late, earlyProperties, lateProperties };
  });
}

function reduceLoadSoyRXForGrowerBegin(state, { shouldClear }) {
  state.isLoading = true;
  state.error = null;
  if (shouldClear) state.fields = [];
}

function reduceLoadSoyRXForGrowerEnd(state, payload) {
  state.isLoading = false;
  state.error = null;
  state.fields = payload;
}

function reduceLoadSoyRXForGrowerError(state, payload) {
  state.isLoading = false;
  state.error = payload;
  state.fields = [];
}

function reduceSetIsLoading(state, payload) {
  state.isLoading = payload;
}

function reduceSetAdjustmentField(state, payload) {
  state.adjustmentField = payload;
}

function reduceLoadLayerMetaComplete(state, payload) {
  const { early, late, earlyProperties, lateProperties } = payload;
  state.layerColors = { early, late };
  state.groupBreaks = {
    early: earlyProperties.groupBreaks,
    late: lateProperties.groupBreaks,
  };
}

const reducerHandlers = {
  [LOAD_SOYRX_FOR_GROWER_BEGIN]: reduceLoadSoyRXForGrowerBegin,
  [LOAD_SOYRX_FOR_GROWER_END]: reduceLoadSoyRXForGrowerEnd,
  [LOAD_SOYRX_FOR_GROWER_ERROR]: reduceLoadSoyRXForGrowerError,
  [LOAD_COLORS.complete]: reduceLoadLayerMetaComplete,
  [SET_IS_LOADING]: reduceSetIsLoading,
  [SET_ADJUSTMENT_FIELD]: reduceSetAdjustmentField,
};

export const reducers = {
  [SLICE]: (state = initialState, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};
